/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
// import SidenavList from "examples/Sidenav/SidenavList";
// import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Icons
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChatIcon from "@mui/icons-material/Chat";
import CallIcon from "@mui/icons-material/Call";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import IntercomComponent from "components/Intercom";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  // const [openCollapse, setOpenCollapse] = useState(false);
  // const [openNestedCollapse, setOpenNestedCollapse] = useState(false);

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const { user, clients } = controller;
  // const items = pathname.split("/").slice(1);
  // const itemParentName = items[1];
  // const itemName = items[items.length - 1];
  const { t } = useTranslation();

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const handleOpenIntercom = () => {
    if (window.Intercom && typeof window.Intercom === "function") {
      window.Intercom("show");
    }
  };

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  // useEffect(() => {
  //   setOpenCollapse(collapseName);
  //   setOpenNestedCollapse(itemParentName);
  // }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, activitiesToView }) => {
      let returnValue;

      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={t(`sidenav.${name}`)}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
              activitiesToView={activitiesToView}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse
              name={t(`sidenav.${name}`)}
              icon={icon}
              active={key === collapseName}
              activitiesToView={activitiesToView}
            />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider sx={{ margin: 0.5 }} key={key} />;
      }
      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      {/* Sidenavbar header */}
      <MDBox pt={6} pb={6} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="100%" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          />
        </MDBox>
      </MDBox>
      {/* Routes */}
      <List>{renderRoutes}</List>
      {/* Bottom */}
      <MDBox
        sx={{
          width: "100%",
          /* position: "absolute", bottom: "25px" */ marginTop: "70px",
          paddingBottom: "20px",
        }}
      >
        <MDBox sx={{ width: "fit-content", margin: "auto" }}>
          {miniSidenav ? (
            <SupportAgentIcon fontSize="medium" />
          ) : (
            <>
              <MDTypography variant="h6" fontSize="small" pb={1}>
                {t("sidenav.support")}
              </MDTypography>
              <MDBox sx={{ display: "flex" }}>
                <CallIcon sx={{ width: 14, marginRight: "10px" }} />
                <MDTypography variant="body1" fontSize="small">
                  +39 02 8717 8377
                </MDTypography>
              </MDBox>
              <MDBox sx={{ display: "flex" }}>
                <MailOutlineIcon sx={{ width: 14, marginRight: "10px" }} />
                <MDTypography variant="body1" fontSize="small">
                  help@defenda.support
                </MDTypography>
              </MDBox>
              {window.Intercom && typeof window.Intercom === "function" && (
                <MDBox onClick={handleOpenIntercom} sx={{ display: "flex", cursor: "pointer" }}>
                  <ChatIcon sx={{ width: 14, marginRight: "10px" }} />
                  <MDTypography variant="body1" fontSize="small">
                    Chat
                  </MDTypography>
                </MDBox>
              )}
            </>
          )}
        </MDBox>
      </MDBox>
      {user && (
        <IntercomComponent user={user} pro={clients?.some((e) => e?.serviceDetail?.length > 0)} />
      )}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  // eslint-disable-next-line
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
